import 'ui/form-group/form-group';
import h from 'hyperscript';
import './heatmap-configuration.css';
import {
  opacityAsFractionToTransparencyAsPercent,
  transparencyAsPercentToOpacityAsFraction
} from 'cadenza/map/layer-configuration-panel/layer-config-utils';
import { SliderNumberInput } from 'ui/slider-number-input/slider-number-input';
import { Select } from 'ui/select/select';
import { debounce } from 'cadenza/utils/debounce';

import i18n from './heatmap-config.properties';

const COMPONENT_NAME = 'd-heatmap-config';
const RENDERING_QUALITIES = [ 'HIGHEST', 'HIGH', 'MEDIUM', 'LOW', 'LOWEST' ];

// see net.disy.gis.layer.configuration.atoms.content.heatmap.HeatmapAggregationRadius.Unit
const RADIUS_UNIT = [ 'METER', 'PIXEL' ];

const RADIUS_MIN = 1;
const RADIUS_MAX = 1000000;

export class HeatmapConfiguration extends HTMLElement {

  #model;

  // input elements:
  #renderingQualitySelect;
  #transparencyInput;
  #baseGradientSelect;

  _radius;
  _radiusUnit;

  constructor (model) {
    super();
    this.#model = model;
  }

  connectedCallback () {
    this.classList.add(COMPONENT_NAME);
    this._render();
  }

  /**
   * @return {HeatMapDto}
   */
  get state () {
    if (!this.#model) {
      return null;
    }

    return {
      renderingQuality: this.#renderingQualitySelect.value,
      transparencyFactor: transparencyAsPercentToOpacityAsFraction(Number(this.#transparencyInput.value)),
      gradient: this.#model.availableGradients[this.#baseGradientSelect.selection.pop().id],
      radius: {
        value: this._radius.value,
        unit: this._radiusUnit.selection.pop().id
      }
    };
  }

  _render () {
    this.innerHTML = '';
    this.#renderingQualitySelect = this._createRenderingQualitySelect();
    const transparencyValue = opacityAsFractionToTransparencyAsPercent(this.#model.transparencyFactor);
    this.#transparencyInput = new SliderNumberInput({
      min: 0,
      max: 100,
      value: transparencyValue,
      unit: '%',
      required: false,
      emitEventOnEveryChange: true
    });
    this.#baseGradientSelect = new Select({
      data: this._createGradientOptions()
    });

    this._radiusUnit = new Select({
      data: this._createRadiusUnitOptions(),
      required: true,
      styleClass: 'radius-unit'
    });

    const debouncedChange = debounce(() => {
      this.dispatchEvent(new CustomEvent('change', { bubbles: true }));
    });

    this._radius = h('input.form-control.radius-value', {
      type: 'number',
      step: 'any',
      min: RADIUS_MIN,
      max: RADIUS_MAX,
      value: this.#model.radius.value,
      oninput: (event) => {
        event.stopPropagation();
        if (this._radius.validity.valid) {
          debouncedChange();
        }
      }
    });

    this.append(
      h('form-group.renderingQuality', [
        h('label', i18n('renderingQuality')),
        this.#renderingQualitySelect
      ]),
      h('form-group', [
        h('label', i18n('transparency')),
        this.#transparencyInput
      ]),
      h('form-group', [
        h('label', i18n('colorMapping')),
        this.#baseGradientSelect
      ]),
      h('form-group', [
        h('label', i18n('radius')),
        h('.d-stack-h.space-1', [
          this._radius,
          this._radiusUnit
        ])
      ])
    );

    this.#renderingQualitySelect.value = this.#model.renderingQuality;
    this._radiusUnit.value = this.#model.radius.unit;

    this.#transparencyInput.value = transparencyValue;
  }

  _createRadiusUnitOptions () {
    return RADIUS_UNIT.map(key => ({
      id: key,
      text: i18n(`radiusUnit.${key}`),
      selected: this.#model.radius.unit === key
    }));
  }

  _createRenderingQualitySelect () {
    return h('select', createOptionElements(
      RENDERING_QUALITIES.map(value => ({ value, label: i18n(`renderingQuality.${value}`) }))));
  }

  _createGradientOptions () {
    if (!this.#model) {
      return [];
    }
    const availableGradients = this.#model.availableGradients;
    const currentGradient = this.#model.gradient.name;
    return Object.entries(availableGradients).map(([ key, value ]) => {
      const icon = h('span.d-icon', {
        style: `background-image: ${createGradient(value.colors)}`
      });
      return {
        id: key,
        text: value.name,
        selected: (currentGradient === value.name),
        icon
      };
    });
  }

}

customElements.define(COMPONENT_NAME, HeatmapConfiguration);

function createOptionElements (options) {
  return options.map(({ value, label }) => h('option', { value }, label));
}

function createGradient (colors) {
  const colorWidth = 100 / colors.length;
  const colorStops = colors.map((color, i) => `${color} ${i * colorWidth}%, ${color} ${(i + 1) * colorWidth}%`);
  return `linear-gradient(to right, ${colorStops.join(', ')})`;
}
